import { CalloutComponent, FormValues, getValue, InputFormCallout, InputFormGroup } from "o4a-react";
import * as React from "react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { IntegerInput } from "../../components/IntegerInput/IntegerInput";
import { parseId } from "../../helpers/idHelper";
import { mdsDataStorageSizeMaxValue, mdsDataStorageSizeMinValue } from "../../helpers/resourceHelper";
import { useMutationCall } from "../../hooks/useMutationCall";
import {
  AsyncNotificationMethodKey,
  AsyncNotificationPolledResponseKey,
  MDS_UPDATE_POLL_DELAY,
  MDS_UPDATE_POLL_INTERVAL,
} from "../../models/AsyncNotificationProviders";
import { getOciRegion } from "../../utils";
import { OperationActionProps } from "../OperationTypes";

enum Fields {
  DataStorageSize = "dataStorageSize",
}

export interface MysqlEditStorageProps extends OperationActionProps {
  databaseId: string | undefined;
  dataStorageSizeInGBs: number | undefined;
}

const MysqlEditStorage = (
  { targetId, location, databaseId, dataStorageSizeInGBs, onExecute, onCancel }: MysqlEditStorageProps,
): JSX.Element => {
  const { subscriptionId, resourceGroup, resourceName } = parseId(databaseId);
  const resourceGroupName = decodeURIComponent(resourceGroup || "");
  const dbSystemName = decodeURIComponent(resourceName || "");

  const [calloutRef, setCalloutRef] = React.useState<CalloutComponent>({} as CalloutComponent);
  const { invokeCall: invokeMdsUpdate } = useMutationCall(
    { method: apiClients.withRegion(getOciRegion(location)).mdsDatabaseApi.updateMdsDbSystem },
  );

  const onUpdate = (formValues: FormValues): void => {
    const storageSize = getValue(formValues, Fields.DataStorageSize, InputFormGroup);
    const invokeOptions = {
      onSuccess: onExecute,
      onFailure: calloutRef?.allowResubmit,
      notification: {
        inProgress: {
          title: Messages.notifications.inProgress.titles.updateMysqlDataStorageSize(),
          message: Messages.notifications.inProgress.messages.updateMysqlDataStorageSize(dbSystemName),
        },
        success: {
          title: Messages.notifications.success.titles.updateMysqlDataStorageSize(),
          message: Messages.notifications.success.messages.updateMysqlDataStorageSize(dbSystemName),
        },
        failure: {
          title: Messages.notifications.failure.titles.updateMysqlDataStorageSize(),
          message: Messages.notifications.failure.messages.updateMysqlDataStorageSize(dbSystemName),
        },
        asyncPolling: {
          methodKey: AsyncNotificationMethodKey.MDS_GET,
          methodArgs: {
            subscriptionId,
            resourceGroupName,
            dbSystemName,
          },
          location,
          responseMethodKey: AsyncNotificationPolledResponseKey.MDS_UPDATED_CHECK,
          delay: MDS_UPDATE_POLL_DELAY,
          interval: MDS_UPDATE_POLL_INTERVAL,
        },
      },
    };
    invokeMdsUpdate({
      subscriptionId,
      resourceGroupName,
      dbSystemName,
      updateMdsDbSystemDetails: { dataStorageSizeInGBs: storageSize },
    }, invokeOptions);
  };

  return (
    <InputFormCallout
      targetId={targetId}
      title={Messages.createNewPanels.editDataStorageSize.title()}
      componentRef={setCalloutRef}
      onSubmit={onUpdate}
      onClose={onCancel}
    >
      <IntegerInput
        fieldName={Fields.DataStorageSize}
        label={Messages.labels.dataStorageSize()}
        defaultValue={dataStorageSizeInGBs}
        min={dataStorageSizeInGBs || mdsDataStorageSizeMinValue}
        max={mdsDataStorageSizeMaxValue}
        required
      />
    </InputFormCallout>
  );
};

export const newMysqlEditStorage = (
  props: MysqlEditStorageProps,
): JSX.Element => (<MysqlEditStorage {...props} />);
